import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { withFieldProps } from 'hooks/useFieldProps';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import groupActions from 'core/groups/actions';
import ApplePay from 'mobileWallet/ApplePay';
import GooglePay from 'mobileWallet/GooglePay';
import { withStyles } from 'theme/withStyles';
import radioStyles from 'styles/radio';
import isIE11 from 'utils/isIE11';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';
import isMobileDevice from 'utils/isMobileDevice';
import Spinner from 'common/Spinner';
import card from './icons/fundingCard.svg';
import cash from './icons/fundingCash.svg';
import transfer from './icons/fundingTransfer.svg';

const styles = (theme) => {
  const { actions, links, palette, screenSizes, spacing, typography } = theme;
  return {
    desktop: {
      display: 'none',
    },
    disabled: {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
    legend: {
      ...typography.h2,
      padding: [spacing.sm, 0],
      textAlign: 'center',
    },
    mobile: {
      '& .help-text p:last-child': {
        marginBottom: 0,
      },
      '& .label-wrap': {
        [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
          '& small': {
            display: 'block',
            marginLeft: 0,
            width: '100%',
          },
          display: 'block',
          width: '100%',
        },
        '& small': {
          ...typography.p,
          ...typography.p3,
          marginLeft: spacing.unit * 2,
          verticalAlign: 'bottom',
          [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
            display: 'block',
            marginLeft: 0,
            width: '100%',
          },
        },
        '& span': {
          color: links.link.color,
          display: 'inline',
          fontFamily: actions.callToAction.fontFamily,
          fontSize: 24,
          fontWeight: typography.fontWeights.medium,
          verticalAlign: 'middle',
        },
        cursor: 'pointer',
      },
      '& i': {
        '&[data-name="AUTOMATIC_DEBIT"]': {
          background: isIE11 ? `transparent url(${transfer}) 0 50% no-repeat` : palette.icon,
          backgroundSize: '100%',
          filter: 'FlipH',
          maskImage: isIE11 ? 'none' : `url(${transfer})`,
          transform: 'scaleX(-1)',
        },
        '&[data-name="CASH"]': {
          background: isIE11 ? `transparent url(${cash}) 0 50% no-repeat` : palette.icon,
          backgroundSize: '100%',
          maskImage: isIE11 ? 'none' : `url(${cash})`,
        },
        '&[data-name="CHECK"]': {
          background: isIE11 ? `transparent url(${cash}) 0 50% no-repeat` : palette.icon,
          backgroundSize: '100%',
          maskImage: isIE11 ? 'none' : `url(${cash})`,
        },
        '&[data-name="INTERNAL_TRANSFER"]': {
          background: isIE11 ? `transparent url(${transfer}) 0 50% no-repeat` : palette.icon,
          backgroundSize: '100%',
          maskImage: isIE11 ? 'none' : `url(${transfer})`,
        },
        '&[data-name="PAYMENT_CARD"]': {
          background: isIE11 ? `transparent url(${card}) 0 50% no-repeat` : palette.icon,
          backgroundSize: '100%',
          maskImage: isIE11 ? 'none' : `url(${card})`,
        },
        height: 32,
        left: 0,
        position: 'absolute',
        top: -2,
        width: 32,
      },
      '&.mobile .spinner': {
        alignItems: 'center',
        borderRadius: '50%',
        display: 'flex !important',
        justifyContent: 'center',
        position: 'absolute',
        right: 10,
        top: 0,
      },
      '&.no-border': {
        borderBottom: 'none',
      },
      '&:first-of-type': {
        marginTop: spacing.xs,
      },
      background: 'none',
      border: 'none',
      borderBottom: `1px solid ${palette.grey.light}`,
      cursor: 'pointer',
      margin: [0, 0, spacing.xs, 0],
      padding: [0, 0, spacing.xs, 50],
      position: 'relative',
      textAlign: 'left',
      width: '100%',
    },
    radio: radioStyles(theme),
  };
};

const FundingTypeSelector = ({
  classes,
  error,
  fieldDomId,
  hasTheme,
  help,
  label,
  loading,
  name,
  onChange,
  onMobileNext,
  options,
  readOnly,
  required,
  tooltip,
  validate,
  value,
}) => {
  const tooltipId = `tooltip-${name}`;
  const errorId = `error-${name}`;
  const helpId = `help-${name}`;
  /* eslint-disable-next-line */
  const describedByString = classnames({ [tooltipId]: tooltip, [errorId]: error }) || undefined;
  const { choices, mobileWalletOption } =
    options?.reduce(
      (obj, option) => {
        if (option.value === 'MOBILE_WALLET') {
          return {
            ...obj,
            mobileWalletOption: option,
          };
        }

        obj.choices.push(option);
        return obj;
      },
      { choices: [], mobileWalletOption: null },
    ) || {};

  return (
    <div
      id={fieldDomId}
      className={classnames(classes.fieldset, {
        optional: !required,
        required,
        [classes.disabled]: hasTheme && loading,
      })}
      role={isMobileDevice() ? 'group' : 'radiogroup'}
      aria-labelledby={label ? `legend-${name}` : undefined}
      aria-describedby={describedByString || undefined}
      aria-label="Funding method"
    >
      <RawHtml type="legend" className={classes.legend} id={`legend-${name}`}>
        {label}
      </RawHtml>
      <Tooltip id={tooltipId} tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
      <Help help={help} id={helpId} />
      <ErrorMsg id={errorId} name={name} />
      {choices?.map((option, index) => (
        <Fragment key={`${fieldDomId}-${option.value}`}>
          <div
            role="button"
            tabIndex="0"
            aria-describedby={classnames({
              [`${helpId}-${option.value}`]: !!help,
              [errorId]: !!error,
            })}
            className={classnames(
              'radio form-group mobile',
              {
                'no-border': index === options.length - 1,
              },
              classes.mobile,
            )}
            onClick={() => {
              onChange(name, option.value);
              onMobileNext();
            }}
          >
            {hasTheme && loading && value === option.value ? (
              <Spinner loading />
            ) : (
              <i data-name={option.value} aria-hidden="true" />
            )}
            <span className={classnames('label-wrap', classes.labelWrap)}>
              <RawHtml type="span">{option.label}</RawHtml>
              {option.maxLimit ? <RawHtml type="small">{` ${option.labelDescr}`}</RawHtml> : null}
            </span>
            <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
          </div>
          <div
            className={classnames(
              'radio form-group desktop',
              {
                'no-border': index === options.length - 1,
              },
              classes.desktop,
            )}
          >
            <input
              type="radio"
              aria-describedby={classnames({
                [`${helpId}-${option.value}`]: option.helpText,
              })}
              name={name}
              checked={value === option.value}
              onChange={() => {
                onChange(name, option.value);
                validate();
              }}
              id={`${fieldDomId}-${option.value}`}
              disabled={readOnly}
              value={option.value}
            />
            <label htmlFor={`${fieldDomId}-${option.value}`}>
              <RawHtml type="span">{option.label}</RawHtml>
              {option.maxLimit ? <RawHtml type="small">{` ${option.labelDescr}`}</RawHtml> : null}
            </label>
            <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}-${option.value}`} />
            <Help help={option.helpText} id={`${helpId}-${option.value}`} />
          </div>
        </Fragment>
      ))}
      {mobileWalletOption && (
        <div className="mobile-options">
          {window.ApplePaySession && <ApplePay options={mobileWalletOption} />}
          <GooglePay options={mobileWalletOption} />
        </div>
      )}
    </div>
  );
};

FundingTypeSelector.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  hasTheme: PropTypes.bool.isRequired,
  help: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onMobileNext: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

const mapStateToProps = ({ fields, ui }, { name }) => ({
  hasTheme: ui.hasTheme,
  loading: ui.appLoading || ui.pageLoading || ui.groupLoading || false,
  options: fields[name].selectorValues,
});

const mapDispatchToProps = (dispatch, { groupName }) => ({
  onMobileNext: () => {
    dispatch(groupActions.next(groupName));
  },
});

const FundingTypeSelectorStyled = withStyles(styles)(FundingTypeSelector);

export default withFieldProps(mapStateToProps, mapDispatchToProps)(FundingTypeSelectorStyled);
